import { Share2, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';
import type { MetadataFieldState } from '../postgenEvidence';
import { MetadataFieldChip } from './MetadataFieldChip';
import { AiSuggestButton } from './AiSuggestButton';

export interface SocialMetadataCardProps {
  /** Current OG title override value. */
  ogTitle: string;
  /** Current OG description override value. */
  ogDescription: string;
  /** Current Twitter title override value. */
  twitterTitle: string;
  /** Current Twitter description override value. */
  twitterDescription: string;
  /** SEO meta title — used as the OG/Twitter title placeholder fallback. */
  metaTitle: string;
  /** SEO meta description — used as the OG/Twitter description placeholder fallback. */
  metaDescription: string;
  onOgTitleChange: (value: string) => void;
  onOgDescriptionChange: (value: string) => void;
  onTwitterTitleChange: (value: string) => void;
  onTwitterDescriptionChange: (value: string) => void;
  /**
   * Optional per-field provenance state (E0 evidence) keyed by metadataPatch
   * field name. The mount supplies this; absent → no chips render.
   */
  fieldStates?: Record<string, MetadataFieldState>;
  /** Content ID for the per-field suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the per-field suggest endpoint. */
  tenantId?: string;
  /** Disable suggest while whole-run generation is in flight. */
  metadataGenerating?: boolean;
}

function SoftCharCounter({ current, max }: { current: number; max: number }) {
  const isOver = current > max;
  return (
    <span
      className={cn(
        'font-mono text-[10.5px] font-semibold',
        isOver ? 'text-destructive' : 'text-muted-foreground',
      )}
      aria-live="polite"
    >
      {current}/{max}
    </span>
  );
}

export function SocialMetadataCard({
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  metaTitle,
  metaDescription,
  onOgTitleChange,
  onOgDescriptionChange,
  onTwitterTitleChange,
  onTwitterDescriptionChange,
  fieldStates,
  contentId,
  tenantId,
  metadataGenerating,
}: SocialMetadataCardProps) {
  const effectivePreviewTitle =
    ogTitle || metaTitle || 'Your social title will appear here';
  const effectivePreviewDescription =
    ogDescription || metaDescription || 'Your social description will appear here';
  const previewDomain =
    tenantId?.replace(/^https?:\/\//, '').replace(/\/+$/, '') || 'Website domain';

  const inputClassName =
    'w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none transition-shadow focus:border-coral focus:ring-2 focus:ring-coral/20';
  const textareaClassName = cn(inputClassName, 'resize-y');

  return (
    <Card railSection="social">
      <CardHeader icon={<Share2 className="size-3.5" />} title="Social" />

      <div className="px-3 pb-3">
        <p className="text-[11px] text-muted-foreground mb-2">
          Leave empty to use the SEO title/description.
        </p>

        <div className="flex flex-col gap-2.5">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Open Graph title
                </span>
                <MetadataFieldChip state={fieldStates?.og_title} />
                {contentId && tenantId && (
                  <AiSuggestButton field="og_title" onSuggest={onOgTitleChange} currentValue={ogTitle} contentId={contentId} tenantId={tenantId} disabled={metadataGenerating} />
                )}
              </span>
              <SoftCharCounter current={ogTitle.length} max={88} />
            </div>
            <input
              type="text"
              value={ogTitle}
              onChange={(e) => onOgTitleChange(e.target.value)}
              placeholder={metaTitle || 'Title shown when shared on Facebook/LinkedIn'}
              className={inputClassName}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Open Graph description
                </span>
                <MetadataFieldChip state={fieldStates?.og_description} />
                {contentId && tenantId && (
                  <AiSuggestButton field="og_description" onSuggest={onOgDescriptionChange} currentValue={ogDescription} contentId={contentId} tenantId={tenantId} disabled={metadataGenerating} />
                )}
              </span>
              <SoftCharCounter current={ogDescription.length} max={200} />
            </div>
            <textarea
              value={ogDescription}
              onChange={(e) => onOgDescriptionChange(e.target.value)}
              placeholder={
                metaDescription || 'Description shown when shared on Facebook/LinkedIn'
              }
              rows={3}
              className={textareaClassName}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Twitter title
                </span>
                <MetadataFieldChip state={fieldStates?.twitter_title} />
                {contentId && tenantId && (
                  <AiSuggestButton field="twitter_title" onSuggest={onTwitterTitleChange} currentValue={twitterTitle} contentId={contentId} tenantId={tenantId} disabled={metadataGenerating} />
                )}
              </span>
              <SoftCharCounter current={twitterTitle.length} max={70} />
            </div>
            <input
              type="text"
              value={twitterTitle}
              onChange={(e) => onTwitterTitleChange(e.target.value)}
              placeholder={metaTitle || 'Title shown when shared on X/Twitter'}
              className={inputClassName}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Twitter description
                </span>
                <MetadataFieldChip state={fieldStates?.twitter_description} />
                {contentId && tenantId && (
                  <AiSuggestButton field="twitter_description" onSuggest={onTwitterDescriptionChange} currentValue={twitterDescription} contentId={contentId} tenantId={tenantId} disabled={metadataGenerating} />
                )}
              </span>
              <SoftCharCounter current={twitterDescription.length} max={200} />
            </div>
            <textarea
              value={twitterDescription}
              onChange={(e) => onTwitterDescriptionChange(e.target.value)}
              placeholder={
                metaDescription || 'Description shown when shared on X/Twitter'
              }
              rows={3}
              className={textareaClassName}
            />
          </div>

          <div className="border border-border rounded-md overflow-hidden bg-card mt-1">
            <div className="aspect-[1.91/1] bg-warm-bg flex items-center justify-center">
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <ImageIcon className="size-4 opacity-50" />
                <span className="text-[10px] text-muted-foreground">Featured image</span>
              </div>
            </div>
            <div className="p-2.5 space-y-0.5">
              <p className="text-[10px] text-muted-foreground uppercase">{previewDomain}</p>
              <p className="text-[12px] font-semibold text-foreground line-clamp-2">
                {effectivePreviewTitle}
              </p>
              <p className="text-[11px] text-muted-foreground line-clamp-2">
                {effectivePreviewDescription}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
